/*
 * SundayBuilder · Screen 1 · Proposition
 * ---------------------------------------------------------------------
 * Renders the three states of the Planning Desk:
 *   State A · resolved             → next Sunday card + Prepare CTA
 *   State B · draft_in_progress    → same card + Continue draft + Start over
 *   State C · profile_incomplete   → Complete-Profile card + manual date fallback
 *
 * Never fabricates. All liturgical values come from the /next-sunday
 * envelope. Unknown fields are simply omitted from the DOM.
 * =================================================================== */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Calendar } from "lucide-react";
import StartOverConfirm from "./StartOverConfirm";
import LargerArcs from "./LargerArcs";

const _MONTHS = ["January", "February", "March", "April", "May", "June",
                 "July", "August", "September", "October", "November", "December"];

function _colorClass(color) {
  if (!color) return "not-set";
  return String(color).toLowerCase().replace(/\s+/g, "_");
}

function _colorLabel(color) {
  if (!color) return "Not set";
  const c = String(color).toLowerCase();
  if (c.includes("green"))     return "Green";
  if (c.includes("violet"))    return "Violet";
  if (c.includes("red"))       return "Red";
  if (c.includes("white"))     return "White & Gold";
  if (c.includes("rose"))      return "Rose";
  if (c.includes("black"))     return "Black";
  return color.charAt(0).toUpperCase() + color.slice(1);
}

function _formatDateFromIso(iso) {
  // "2026-02-15" → "February 15"
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${_MONTHS[m - 1]} ${d}`;
}

/* ---------- State A / B · resolved ---------- */
function AlreadyKnownStrip({ context }) {
  // Rule 0.3 · Quiet confirmation, not a checklist. Every row is
  // omitted when its data is absent — never rendered as 'Unknown'.
  if (!context) return null;
  const rows = [];
  if (context.parish_name)        rows.push(["Parish", context.parish_name]);
  if (context.tradition)          rows.push(["Tradition", context.tradition]);
  if (context.lectionary)         rows.push(["Lectionary", context.lectionary]);
  if (context.season_and_color)   rows.push(["Season & color", context.season_and_color]);
  if (context.primary_celebrant)  rows.push(["Primary celebrant", context.primary_celebrant]);
  if (context.choir_rehearsal)    rows.push(["Choir rehearsal", context.choir_rehearsal]);
  if (Array.isArray(context.hymnals) && context.hymnals.length > 0)
    rows.push(["Hymnal", context.hymnals.join(", ")]);
  if (Array.isArray(context.languages) && context.languages.length > 0)
    rows.push(["Worship language", context.languages.join(", ")]);
  if (rows.length === 0) return null;
  return (
    <div className="sb-known" data-testid="sb-already-known-strip" aria-label="Already known">
      <p className="sb-known__label">The platform already knows</p>
      <dl className="sb-known__list">
        {rows.map(([k, v]) => (
          <div className="sb-known__row" key={k}>
            <dt>{k}</dt>
            <dd data-testid={`sb-known-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function GuestMinistryLink() {
  // Rule 0.3 · quiet secondary action, positioned below LargerArcs so
  // it never competes with the primary "Prepare this Sunday" CTA.
  const navigate = useNavigate();
  return (
    <div className="sb-guest-link" data-testid="sb-guest-ministry-link">
      <p className="sb-guest-link__lead">Serving somewhere else?</p>
      <button
        className="sb-guest-link__cta"
        onClick={() => navigate("/guest-ministry")}
        data-testid="sb-guest-ministry-cta"
      >
        Prepare a Guest Ministry dossier →
      </button>
    </div>
  );
}

function ResolvedCard({ envelope, onPrepare, onContinueDraft, onStartOver }) {
  const s = envelope.next_sunday;
  const isDraft = envelope.state === "draft_in_progress";
  const statusLabel = s.status === "draft" ? "Draft in progress"
                    : s.status === "ready" ? "Ready"
                    : "Not yet prepared";
  const primaryLabel = isDraft ? "Continue draft" : "Prepare this Sunday";
  const primaryHandler = isDraft ? onContinueDraft : onPrepare;

  return (
    <div
      className="sb-proposition"
      data-testid="sb-proposition-card"
      aria-labelledby="sb-proposition-heading"
    >
      <h2 id="sb-proposition-heading" className="sb-proposition__date" data-testid="sb-proposition-date">
        {s.label}
        {isDraft && (
          <span className="sb-draft-badge" data-testid="sb-proposition-draft-badge">
            draft in progress
          </span>
        )}
      </h2>
      <p className="sb-proposition__occasion" data-testid="sb-proposition-occasion">
        {s.occasion}
      </p>
      {s.liturgical_color && (
        <p className="sb-proposition__color" data-testid="sb-proposition-color">
          <span className={`sb-proposition__color-dot ${_colorClass(s.liturgical_color)}`} aria-hidden="true" />
          {_colorLabel(s.liturgical_color)}
        </p>
      )}

      <hr className="sb-proposition__rule" />

      <dl className="sb-proposition__meta">
        {Array.isArray(s.readings) && s.readings.length > 0 && (
          <>
            <dt>Readings</dt>
            <dd data-testid="sb-proposition-readings">
              {s.readings.map((r, i) => <div key={i}>{r}</div>)}
              {s.psalm && <div>{s.psalm}</div>}
            </dd>
          </>
        )}
        {s.preacher && (
          <>
            <dt>Preacher</dt>
            <dd data-testid="sb-proposition-preacher">
              {s.preacher}
              {s.preacher_source === "parish_profile" && (
                <span className="sub">from Parish Profile</span>
              )}
            </dd>
          </>
        )}
        <dt>Status</dt>
        <dd>
          <span className="sb-proposition__status" data-testid="sb-proposition-status">
            <span className={`sb-proposition__status-dot ${s.status || "not-set"}`} aria-hidden="true" />
            {statusLabel}
          </span>
        </dd>
      </dl>

      <div className="sb-proposition__actions">
        {isDraft && (
          <button
            className="sb-btn destructive-ghost"
            onClick={onStartOver}
            data-testid="sb-start-over"
          >
            Start over
          </button>
        )}
        <button
          className="sb-btn primary"
          onClick={primaryHandler}
          data-testid={isDraft ? "sb-continue-draft" : "sb-prepare-this-sunday"}
        >
          {primaryLabel}
          <ChevronRight size={18} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

/* ---------- State C · profile_incomplete ---------- */
function IncompleteCard({ envelope, onManualDate }) {
  const navigate = useNavigate();
  const [showManual, setShowManual] = useState(false);
  const [manualDate, setManualDate] = useState("");
  const missing = envelope.missing_profile_fields || [];
  const labelFor = (f) => ({
    parish_name:   "Parish name",
    denomination:  "Denomination",
    lectionary:    "Lectionary",
  }[f] || f);

  const isValidSunday = (iso) => {
    if (!iso) return false;
    const d = new Date(iso + "T12:00:00Z");
    return d.getUTCDay() === 0;
  };

  return (
    <div className="sb-incomplete" data-testid="sb-profile-incomplete-card">
      <h2 className="sb-incomplete__title">Complete your Parish Profile</h2>
      <p className="sb-incomplete__sub">to unlock automatic Sunday planning</p>
      <hr className="sb-incomplete__hairline" />
      <p className="sb-incomplete__body">
        Automatic liturgical context depends on your parish's denomination,
        primary lectionary, and calendar preferences. Once complete,
        SundayBuilder will surface your next Sunday every time you open it.
      </p>
      {missing.length > 0 && (
        <>
          <p className="sb-incomplete__missing">What's missing</p>
          <ul className="sb-incomplete__missing-list">
            {missing.map((f) => (
              <li key={f}>
                {labelFor(f)} <span className="not-set">Not set</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="sb-incomplete__actions">
        <button
          className="sb-btn primary"
          onClick={() => navigate("/parish-profile")}
          data-testid="sb-complete-profile-cta"
        >
          Complete Parish Profile
          <ChevronRight size={18} strokeWidth={2.5} />
        </button>
        {!showManual ? (
          <button
            className="sb-manual-link"
            onClick={() => setShowManual(true)}
            data-testid="sb-manual-date-link"
          >
            Or enter a date manually →
          </button>
        ) : (
          <div className="sb-manual-input-row">
            <label htmlFor="sb-manual-date">Which Sunday?</label>
            <input
              id="sb-manual-date"
              type="date"
              value={manualDate}
              onChange={(e) => setManualDate(e.target.value)}
              data-testid="sb-manual-date-input"
            />
            <button
              className="sb-btn primary"
              style={{ minWidth: "auto", padding: "10px 20px" }}
              disabled={!isValidSunday(manualDate)}
              onClick={() => onManualDate(manualDate)}
              data-testid="sb-manual-date-continue"
            >
              Continue
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Main component ---------- */
export default function Proposition({ envelope, onPrepare, onContinueDraft, onStartOverConfirmed, onManualDate }) {
  const [showConfirm, setShowConfirm] = useState(false);

  if (!envelope || envelope.state === "unavailable") {
    return (
      <div className="sb-incomplete" data-testid="sb-unavailable-card">
        <h2 className="sb-incomplete__title">Preparation is not currently available</h2>
        <p className="sb-incomplete__body">
          The liturgical calendar service is temporarily unavailable. Please try again in a moment.
        </p>
      </div>
    );
  }

  if (envelope.state === "profile_incomplete") {
    return (
      <IncompleteCard envelope={envelope} onManualDate={onManualDate} />
    );
  }

  return (
    <>
      <ResolvedCard
        envelope={envelope}
        onPrepare={onPrepare}
        onContinueDraft={onContinueDraft}
        onStartOver={() => setShowConfirm(true)}
      />

      <AlreadyKnownStrip context={envelope.context} />

      <LargerArcs alternatives={envelope.alternatives || {}} onManualDate={onManualDate} />

      <GuestMinistryLink />

      {showConfirm && envelope.next_sunday && (
        <StartOverConfirm
          savedAt={envelope.next_sunday.draft_saved_at}
          occasion={envelope.next_sunday.occasion}
          onKeep={() => setShowConfirm(false)}
          onDiscard={() => { setShowConfirm(false); onStartOverConfirmed(); }}
        />
      )}
    </>
  );
}

export { _formatDateFromIso };
